import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '../../design-system/theme/reviewDesk';
import { useTranslation } from '../../hooks/useTranslation';
import {
  Cohort,
  UserDocumentItem,
  userDocumentsAPI,
} from '../../services/api';
import MentorTrainingAPI from '../../services/api/mentorTrainingAPI';
import {
  DrillSummary,
  DrillTargetLevel,
  TARGET_LEVELS,
} from '../../types/mentorTraining';

interface PublishDrillModalProps {
  visible: boolean;
  cohorts: Cohort[];
  defaultCohortId: number | null;
  onClose: () => void;
  onCreated: (drill: DrillSummary) => void;
}

export default function PublishDrillModal({
  visible,
  cohorts,
  defaultCohortId,
  onClose,
  onCreated,
}: PublishDrillModalProps) {
  const { t } = useTranslation();

  const [cohortId, setCohortId] = useState<number | null>(defaultCohortId);
  const [docs, setDocs] = useState<UserDocumentItem[]>([]);
  const [docId, setDocId] = useState<number | null>(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [moduleName, setModuleName] = useState('');
  const [targetLevel, setTargetLevel] = useState<DrillTargetLevel>('pcc');
  const [instructions, setInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCohortId(defaultCohortId);
  }, [defaultCohortId]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      setDocsLoading(true);
      try {
        const list = await userDocumentsAPI.list(0, 100, 'user_analysis');
        if (!cancelled) {
          const ready = (list.documents || []).filter((d) => d.processed !== false);
          setDocs(ready);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setTitle('');
      setModuleName('');
      setInstructions('');
      setError(null);
      setDocId(null);
      setSubmitting(false);
    }
  }, [visible]);

  const selectedDoc = useMemo(
    () => docs.find((d) => d.id === docId) || null,
    [docs, docId]
  );

  const canSubmit =
    cohortId !== null &&
    docId !== null &&
    title.trim().length > 0 &&
    !submitting;

  const onPublish = useCallback(async () => {
    if (!canSubmit || cohortId === null || docId === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await MentorTrainingAPI.createDrill({
        cohort_id: cohortId,
        source_document_id: docId,
        title: title.trim(),
        target_level: targetLevel,
        module_name: moduleName.trim() || null,
        instructions: instructions.trim() || null,
        publish_now: true,
      });
      onCreated(created);
      onClose();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, cohortId, docId, title, targetLevel, moduleName, instructions, onCreated, onClose]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {t('mentorTraining.publish.title')}
            </Text>
            <TouchableOpacity onPress={onClose} accessibilityRole="button">
              <MaterialIcons name="close" size={20} color={reviewDesk.muted} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            <View style={styles.field}>
              <Text style={styles.label}>{t('mentorTraining.publish.cohort')}</Text>
              {cohorts.length === 0 ? (
                <Text style={styles.hint}>
                  {t('mentorTraining.publish.noCohorts')}
                </Text>
              ) : (
                <View style={styles.pillsRow}>
                  {cohorts.map((c) => {
                    const active = c.id === cohortId;
                    return (
                      <TouchableOpacity
                        key={c.id}
                        onPress={() => setCohortId(c.id)}
                        style={[styles.pill, active && styles.pillActive]}
                      >
                        <Text style={[styles.pillText, active && styles.pillTextActive]}>
                          {c.name} ({c.member_count})
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>
                {t('mentorTraining.publish.sourceDoc')}
              </Text>
              {docsLoading ? (
                <ActivityIndicator size="small" color={reviewDesk.accent} />
              ) : docs.length === 0 ? (
                <Text style={styles.hint}>
                  {t(
                    'mentorTraining.publish.noDocs'
                  )}
                </Text>
              ) : (
                <ScrollView
                  style={styles.docList}
                  contentContainerStyle={styles.docListContent}
                  nestedScrollEnabled
                  showsVerticalScrollIndicator
                >
                  {docs.map((d) => {
                    const active = docId === d.id;
                    return (
                      <Pressable
                        key={d.id}
                        onPress={() => setDocId(d.id)}
                        style={({ hovered }: any) => [
                          styles.docRow,
                          hovered && { backgroundColor: reviewDesk.cardMuted },
                          active && styles.docRowActive,
                        ]}
                      >
                        <MaterialIcons
                          name={active ? 'radio-button-checked' : 'radio-button-unchecked'}
                          size={18}
                          color={active ? reviewDesk.accent : reviewDesk.muted}
                        />
                        <View style={styles.docInfo}>
                          <Text style={styles.docName} numberOfLines={1}>
                            {d.original_filename}
                          </Text>
                          <Text style={styles.docMeta}>
                            {new Date(d.created_at).toLocaleDateString()}
                            {d.transcript_language ? ` · ${d.transcript_language.toUpperCase()}` : ''}
                          </Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              )}
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>
                {t('mentorTraining.publish.titleLabel')}
              </Text>
              <TextInput
                value={title}
                onChangeText={setTitle}
                placeholder={t(
                  'mentorTraining.publish.titlePlaceholder'
                )}
                placeholderTextColor={reviewDesk.muted}
                style={styles.input}
                maxLength={200}
              />
            </View>

            <View style={styles.fieldRow}>
              <View style={[styles.field, { flex: 1 }]}>
                <Text style={styles.label}>
                  {t('mentorTraining.publish.module')}
                </Text>
                <TextInput
                  value={moduleName}
                  onChangeText={setModuleName}
                  placeholder={t('mentorTraining.publish.modulePlaceholder')}
                  placeholderTextColor={reviewDesk.muted}
                  style={styles.input}
                  maxLength={120}
                />
              </View>
              <View style={[styles.field, { flex: 1 }]}>
                <Text style={styles.label}>
                  {t('mentorTraining.publish.targetLevel')}
                </Text>
                <View style={styles.pillsRow}>
                  {TARGET_LEVELS.map((lvl) => {
                    const active = targetLevel === lvl;
                    return (
                      <TouchableOpacity
                        key={lvl}
                        onPress={() => setTargetLevel(lvl)}
                        style={[styles.pillSm, active && styles.pillSmActive]}
                      >
                        <Text style={[styles.pillSmText, active && styles.pillSmTextActive]}>
                          {lvl.toUpperCase()}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>
                {t('mentorTraining.publish.instructions')}
              </Text>
              <TextInput
                value={instructions}
                onChangeText={setInstructions}
                placeholder={t(
                  'mentorTraining.publish.instructionsPlaceholder'
                )}
                placeholderTextColor={reviewDesk.muted}
                style={[styles.input, styles.textarea]}
                multiline
              />
            </View>

            {!!error && (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity onPress={onClose} style={styles.cancelBtn}>
              <Text style={styles.cancelBtnText}>
                {t('mentorTraining.publish.cancel')}
              </Text>
            </TouchableOpacity>
            <Pressable
              onPress={onPublish}
              disabled={!canSubmit}
              style={({ hovered, pressed }: any) => [
                styles.publishBtn,
                !canSubmit && { opacity: 0.5 },
                hovered && canSubmit && { backgroundColor: '#1F36A0' },
                pressed && { opacity: 0.85 },
              ]}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.publishBtnText}>
                  {t('mentorTraining.publish.confirm')}
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(16, 24, 39, 0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 640,
    maxHeight: '90%',
    backgroundColor: reviewDesk.card,
    borderRadius: 14,
    overflow: 'hidden',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 22,
    paddingVertical: 16,
    borderBottomColor: reviewDesk.line,
    borderBottomWidth: 1,
  },
  title: { fontSize: 17, fontWeight: '700', color: reviewDesk.ink },
  body: { flexGrow: 1 },
  bodyContent: { padding: 22, gap: 18 },
  field: { gap: 6 },
  fieldRow: { flexDirection: 'row', gap: 12 },
  label: { fontSize: 13, fontWeight: '600', color: reviewDesk.inkSoft },
  hint: { fontSize: 12, color: reviewDesk.muted, fontStyle: 'italic' },
  pillsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  pill: {
    paddingHorizontal: 11,
    paddingVertical: 6,
    backgroundColor: reviewDesk.cardMuted,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 16,
  },
  pillActive: { backgroundColor: reviewDesk.accent, borderColor: reviewDesk.accent },
  pillText: { fontSize: 12, color: reviewDesk.inkSoft, fontWeight: '500' },
  pillTextActive: { color: '#fff', fontWeight: '600' },
  pillSm: {
    paddingHorizontal: 11,
    paddingVertical: 5,
    backgroundColor: reviewDesk.cardMuted,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 6,
  },
  pillSmActive: { backgroundColor: reviewDesk.accent, borderColor: reviewDesk.accent },
  pillSmText: { fontSize: 11, fontWeight: '700', color: reviewDesk.inkSoft, letterSpacing: 0.5 },
  pillSmTextActive: { color: '#fff' },
  docList: {
    maxHeight: 220,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 8,
  },
  docListContent: { gap: 4, paddingVertical: 2 },
  docRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  docRowActive: { backgroundColor: reviewDesk.accentSoft },
  docInfo: { flex: 1 },
  docName: { fontSize: 13, color: reviewDesk.ink, fontWeight: '500' },
  docMeta: { fontSize: 11, color: reviewDesk.muted, marginTop: 2 },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: reviewDesk.cardMuted,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 8,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: reviewDesk.ink,
  },
  textarea: { minHeight: 70, textAlignVertical: 'top' },
  errorBox: {
    padding: 10,
    backgroundColor: '#FFE7E1',
    borderRadius: 6,
  },
  errorText: { color: '#7A2B1F', fontSize: 12 },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    paddingHorizontal: 22,
    paddingVertical: 14,
    borderTopColor: reviewDesk.line,
    borderTopWidth: 1,
  },
  cancelBtn: { paddingHorizontal: 14, paddingVertical: 9 },
  cancelBtnText: { fontSize: 13, color: reviewDesk.inkSoft, fontWeight: '500' },
  publishBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: reviewDesk.accent,
    borderRadius: 8,
    minWidth: 140,
    alignItems: 'center',
  },
  publishBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
});
